import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { projectsApi, clientsApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  ChevronRight,
  ChevronDown,
  Building2,
  FolderKanban,
  Phone,
  MapPin,
  FileText,
  User,
  X,
  Star,
} from "lucide-react";
import { cn } from "../lib/utils";
import { getProjectColor } from "../lib/branding";

const Projects = () => {
  const { isAdmin, canManage } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsedClients, setCollapsedClients] = useState({});
  const [favorites, setFavorites] = useState([]);

  // Project form
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({
    name: "", client_id: "", client_name: "", hourly_rate: "", address: "", notes: "", contact_persons: [],
  });

  // Client form
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState({ name: "", address: "", notes: "", contact_persons: [] });

  // Project detail
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetailOpen, setProjectDetailOpen] = useState(false);
  const [projectEntries, setProjectEntries] = useState([]);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [projectsRes, clientsRes] = await Promise.all([
        projectsApi.getAll(),
        clientsApi.getAll(),
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error("Kunde inte ladda data");
    } finally {
      setLoading(false);
    }
    projectsApi.getMyFavorites().then(r => setFavorites(r.data)).catch(() => {});
  };

  const handleToggleFavorite = async (projectId, e) => {
    e.stopPropagation();
    try {
      const res = await projectsApi.toggleFavorite(projectId);
      setFavorites(res.data.favorites);
    } catch { toast.error("Kunde inte uppdatera favorit"); }
  };

  // Group projects by client
  const clientGroups = useMemo(() => {
    const groups = {};
    // First add all known clients
    clients.forEach(c => {
      groups[c.id] = { client: c, projects: [] };
    });
    // Add an "uncategorized" group for projects without client_id
    groups['_none'] = { client: { id: '_none', name: 'Utan kund', address: '', contact_persons: [], notes: '' }, projects: [] };

    projects.forEach(p => {
      const cid = p.client_id || '_none';
      if (!groups[cid]) {
        // client_id exists but no matching client - use client_name
        groups[cid] = { client: { id: cid, name: p.client_name || 'Okänd kund', address: '', contact_persons: [], notes: '' }, projects: [] };
      }
      groups[cid].projects.push(p);
    });

    return Object.values(groups)
      .filter(g => g.projects.length > 0 || g.client.id !== '_none')
      .sort((a, b) => {
        if (a.client.id === '_none') return 1;
        if (b.client.id === '_none') return -1;
        return a.client.name.localeCompare(b.client.name);
      });
  }, [projects, clients]);

  const toggleClient = (clientId) => {
    setCollapsedClients(prev => ({ ...prev, [clientId]: !prev[clientId] }));
  };

  // --- Client CRUD ---
  const openAddClient = () => {
    setEditingClient(null);
    setClientForm({ name: "", work_number: "", address: "", notes: "", contact_persons: [] });
    setClientDialogOpen(true);
  };

  const openEditClient = (client, e) => {
    if (e) e.stopPropagation();
    setEditingClient(client);
    setClientForm({
      name: client.name,
      work_number: client.work_number || "",
      address: client.address || "",
      notes: client.notes || "",
      contact_persons: client.contact_persons || [],
    });
    setClientDialogOpen(true);
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    if (!clientForm.name.trim()) { toast.error("Ange kundnamn"); return; }
    setSubmitting(true);
    try {
      if (editingClient) {
        await clientsApi.update(editingClient.id, clientForm);
        toast.success("Kund uppdaterad");
      } else {
        await clientsApi.create(clientForm);
        toast.success("Kund skapad");
      }
      setClientDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error("Kunde inte spara");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClient = async (clientId, e) => {
    if (e) e.stopPropagation();
    const clientProjects = projects.filter(p => p.client_id === clientId);
    if (clientProjects.length > 0) {
      toast.error(`Kan inte ta bort — ${clientProjects.length} projekt tillhör denna kund`);
      return;
    }
    try {
      await clientsApi.delete(clientId);
      toast.success("Kund borttagen");
      loadData();
    } catch (error) {
      toast.error("Kunde inte ta bort");
    }
  };

  // --- Project CRUD ---
  const openAddProject = (clientId = "") => {
    setEditingProject(null);
    setProjectForm({ name: "", work_number: "", client_id: clientId, client_name: "", hourly_rate: "", address: "", notes: "", contact_persons: [] });
    setProjectDialogOpen(true);
  };

  const openEditProject = (project, e) => {
    if (e) e.stopPropagation();
    setEditingProject(project);
    setProjectForm({
      name: project.name,
      work_number: project.work_number || "",
      client_id: project.client_id || "",
      client_name: project.client_name || "",
      hourly_rate: project.hourly_rate?.toString() || "",
      address: project.address || "",
      notes: project.notes || "",
      contact_persons: project.contact_persons || [],
    });
    setProjectDialogOpen(true);
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    if (!projectForm.name.trim()) { toast.error("Ange projektnamn"); return; }
    setSubmitting(true);
    try {
      const data = {
        ...projectForm,
        hourly_rate: parseFloat(projectForm.hourly_rate) || 0,
      };
      // Set client_name from selected client
      if (data.client_id && data.client_id !== '_none') {
        const client = clients.find(c => c.id === data.client_id);
        if (client) data.client_name = client.name;
      }
      if (editingProject) {
        await projectsApi.update(editingProject.id, data);
        toast.success("Projekt uppdaterat");
      } else {
        await projectsApi.create(data);
        toast.success("Projekt skapat");
      }
      setProjectDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error("Kunde inte spara");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProject = async (projectId, e) => {
    if (e) e.stopPropagation();
    if (!confirm("Ta bort projektet?")) return;
    try {
      await projectsApi.delete(projectId, true);
      toast.success("Projekt borttaget");
      loadData();
    } catch (error) {
      toast.error("Kunde inte ta bort");
    }
  };

  const handleToggleActive = async (project, e) => {
    if (e) e.stopPropagation();
    try {
      await projectsApi.update(project.id, { is_active: !project.is_active });
      loadData();
    } catch (error) {
      toast.error("Kunde inte uppdatera");
    }
  };

  // Project detail - navigate to detail page
  const showProjectDetail = (project) => {
    navigate(`/projects/${project.id}`);
  };

  // Contact person helpers
  const addContactPerson = (formSetter, formState) => {
    formSetter({ ...formState, contact_persons: [...(formState.contact_persons || []), { name: "", phone: "", email: "" }] });
  };

  const updateContactPerson = (formSetter, formState, idx, field, value) => {
    const updated = [...formState.contact_persons];
    updated[idx] = { ...updated[idx], [field]: value };
    formSetter({ ...formState, contact_persons: updated });
  };

  const removeContactPerson = (formSetter, formState, idx) => {
    formSetter({ ...formState, contact_persons: formState.contact_persons.filter((_, i) => i !== idx) });
  };

  const projectStats = useMemo(() => {
    const totalHours = projectEntries.reduce((sum, e) => sum + e.hours, 0);
    const totalAmount = projectEntries.reduce((sum, e) => sum + (e.hours * (e.hourly_rate || 0)), 0);
    const approvedHours = projectEntries.filter(e => e.status === "godkänd").reduce((sum, e) => sum + e.hours, 0);
    return { totalHours, totalAmount, approvedHours };
  }, [projectEntries]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6dbf4a]"></div></div>;
  }

  return (
    <div className="space-y-5" data-testid="projects-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FolderKanban className="w-5 h-5 text-[#6dbf4a]" />
            <h1 className="text-2xl font-bold text-white tracking-tight">Projekt</h1>
          </div>
          <p className="text-white/25 text-sm">Kunder och projektöversikt</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={openAddClient} className="gap-1 border-white/[0.06] text-white/40 hover:text-white" data-testid="add-client-btn">
              <Building2 className="w-4 h-4" /> Ny kund
            </Button>
            <Button onClick={() => openAddProject()} className="gap-1 bg-[#6dbf4a] hover:bg-[#5da83d]" data-testid="add-project-btn">
              <Plus className="w-4 h-4" /> Nytt projekt
            </Button>
          </div>
        )}
      </div>

      {/* Client groups */}
      {clientGroups.length > 0 ? (
        <div className="space-y-4">
          {clientGroups.map(group => {
            const isCollapsed = collapsedClients[group.client.id];
            const activeCount = group.projects.filter(p => p.is_active).length;
            const isRealClient = group.client.id !== '_none';

            return (
              <div key={group.client.id} data-testid={`client-group-${group.client.id}`}>
                {/* Client header */}
                <div
                  className={cn(
                    "flex items-center justify-between px-4 py-3 rounded-t-lg border cursor-pointer transition-colors",
                    isRealClient ? "bg-[#1f1f1f] border-white/[0.06] hover:bg-[#0a0e14]" : "bg-[#080c12] border-white/[0.06]"
                  )}
                  onClick={() => toggleClient(group.client.id)}>
                  <div className="flex items-center gap-3">
                    <div className={cn("transition-transform", isCollapsed ? "" : "rotate-90")}>
                      <ChevronRight className="w-5 h-5 text-white/40" />
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <span className="text-white font-semibold text-lg">{group.client.name}</span>
                      {group.client.work_number && (
                        <Badge variant="outline" className="ml-2 text-blue-400 border-blue-500/30 text-xs">{group.client.work_number}</Badge>
                      )}
                      {group.client.address && (
                        <p className="text-xs text-white/40 flex items-center gap-1"><MapPin className="w-3 h-3" />{group.client.address}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-white/40 border-white/[0.06] ml-2">
                      {activeCount} projekt
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {isRealClient && canManage && (
                      <Button variant="ghost" size="icon" className="h-10 w-10 text-white/50 hover:text-white"
                        onClick={(e) => openEditClient(group.client, e)} data-testid={`edit-client-${group.client.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {canManage && (
                      <Button size="icon" variant="ghost"
                        className="h-10 w-10 text-[#6dbf4a] hover:bg-[#6dbf4a]/20"
                        onClick={(e) => { e.stopPropagation(); openAddProject(group.client.id); }}
                        title="Nytt projekt">
                        <Plus className="w-5 h-5" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Projects under this client */}
                {!isCollapsed && (
                  <Card className="rounded-t-none border-t-0 bg-[#0d1218] border-white/[0.06]">
                    {group.projects.length > 0 ? (
                      <div className="divide-y divide-[#333]">
                        {group.projects.map(project => (
                          <div key={project.id}
                            className={cn("flex items-center gap-3 px-3 sm:px-4 py-3 cursor-pointer hover:bg-[#333]/30 transition-colors",
                              !project.is_active && "opacity-50"
                            )}
                            onClick={() => showProjectDetail(project)}
                            data-testid={`project-row-${project.id}`}>
                            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{ background: `${getProjectColor(project.id)}15` }}>
                              <FolderKanban className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: getProjectColor(project.id) }} />
                            </div>
                            <div className="flex-1 min-w-0 overflow-hidden">
                              <p className="text-white font-medium truncate text-sm sm:text-base">
                                {project.name}
                                {!project.is_active && <span className="ml-1.5 text-[10px] text-white/30 bg-white/5 px-1.5 py-0.5 rounded">Inaktiv</span>}
                              </p>
                              {(project.work_number || project.address || (isAdmin && project.hourly_rate > 0)) && (
                                <p className="text-[11px] text-white/30 truncate mt-0.5">
                                  {project.work_number && <span className="font-mono mr-2" style={{ color: `${getProjectColor(project.id)}80` }}>{project.work_number}</span>}
                                  {isAdmin && project.hourly_rate > 0 && <span className="mr-2">{project.hourly_rate} kr/h</span>}
                                  {project.address && <span>{project.address}</span>}
                                </p>
                              )}
                            </div>
                            {/* Favorite star */}
                            <button onClick={(e) => handleToggleFavorite(project.id, e)}
                              data-testid={`favorite-btn-${project.id}`}
                              className={cn("w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 transition-all",
                                favorites.includes(project.id) ? "text-amber-400" : "text-white/10 hover:text-amber-400/50"
                              )}>
                              <Star className="w-4 h-4" fill={favorites.includes(project.id) ? "currentColor" : "none"} />
                            </button>
                            {canManage && (
                              <div className="flex items-center flex-shrink-0">
                                <button
                                  className="w-10 h-10 flex items-center justify-center rounded-lg text-white/30 hover:text-white hover:bg-white/[0.06] transition-colors"
                                  onClick={(e) => { e.stopPropagation(); openEditProject(project, e); }}>
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  className={cn("w-10 h-10 flex items-center justify-center rounded-lg transition-colors",
                                    project.is_active
                                      ? "text-amber-400 hover:bg-amber-500/15"
                                      : "text-[#6dbf4a] hover:bg-[#6dbf4a]/15"
                                  )}
                                  onClick={(e) => { e.stopPropagation(); handleToggleActive(project, e); }}
                                  title={project.is_active ? "Inaktivera" : "Aktivera"}>
                                  {project.is_active ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                </button>
                              </div>
                            )}
                            <ChevronRight className="w-4 h-4 text-white/20 flex-shrink-0" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-6 text-center text-white/40 text-sm">Inga projekt ännu</div>
                    )}
                  </Card>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <Card className="bg-[#0d1218] border-white/[0.06]">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-white/25" />
            <p className="text-white/40">Inga kunder eller projekt</p>
            <p className="text-sm text-white/40 mt-1">Skapa en kund först, lägg sedan till projekt</p>
          </CardContent>
        </Card>
      )}

      {/* Client Dialog */}
      <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
        <DialogContent className="bg-[#0d1218] border-white/[0.06]" data-testid="client-form-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-400" />
              {editingClient ? "Redigera kund" : "Ny kund"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleClientSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-white/40 text-sm">Kundnamn *</Label>
                <Input value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                  placeholder="T.ex. Kunskapsskolan AB" className="bg-[#080c12] border-white/[0.06] text-white" data-testid="client-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-white/40 text-sm">Kundnummer</Label>
                <Input value={clientForm.work_number} onChange={(e) => setClientForm({ ...clientForm, work_number: e.target.value })}
                  placeholder="T.ex. K-2024-001" className="bg-[#080c12] border-white/[0.06] text-white" data-testid="client-work-number" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-white/40 text-sm">Adress</Label>
              <Input value={clientForm.address} onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })}
                placeholder="Gatan 1, 123 45 Stad" className="bg-[#080c12] border-white/[0.06] text-white" />
            </div>
            <div className="space-y-1">
              <Label className="text-white/40 text-sm">Anteckningar</Label>
              <Input value={clientForm.notes} onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })}
                placeholder="Övrigt..." className="bg-[#080c12] border-white/[0.06] text-white" />
            </div>
            {/* Contact persons */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-white/40 text-sm">Kontaktpersoner</Label>
                <Button type="button" size="sm" variant="outline" onClick={() => addContactPerson(setClientForm, clientForm)}
                  className="border-white/[0.06] text-white/40 text-xs"><Plus className="w-3 h-3 mr-1" />Lägg till</Button>
              </div>
              {clientForm.contact_persons?.map((cp, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input value={cp.name} onChange={(e) => updateContactPerson(setClientForm, clientForm, idx, 'name', e.target.value)}
                    placeholder="Namn" className="bg-[#080c12] border-white/[0.06] text-white flex-1" />
                  <Input value={cp.phone} onChange={(e) => updateContactPerson(setClientForm, clientForm, idx, 'phone', e.target.value)}
                    placeholder="Telefon" className="bg-[#080c12] border-white/[0.06] text-white w-32" />
                  <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-white/40 hover:text-red-400"
                    onClick={() => removeContactPerson(setClientForm, clientForm, idx)}><X className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setClientDialogOpen(false)} className="border-white/[0.06] text-white/40">Avbryt</Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="client-save-btn">
                {submitting ? "..." : "Spara"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Project Dialog */}
      <Dialog open={projectDialogOpen} onOpenChange={setProjectDialogOpen}>
        <DialogContent className="bg-[#0d1218] border-white/[0.06] max-w-lg" data-testid="project-form-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FolderKanban className="w-5 h-5 text-[#6dbf4a]" />
              {editingProject ? "Redigera projekt" : "Nytt projekt"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleProjectSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label className="text-white/40 text-sm">Kund</Label>
              <Select value={projectForm.client_id || "_none"} onValueChange={(v) => setProjectForm({ ...projectForm, client_id: v === "_none" ? "" : v })}>
                <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white" data-testid="project-client-select">
                  <SelectValue placeholder="Välj kund" />
                </SelectTrigger>
                <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                  <SelectItem value="_none" className="text-white/40">Ingen kund</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-white">{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-white/40 text-sm">Projektnamn *</Label>
              <Input value={projectForm.name} onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                placeholder="T.ex. Ventilation Byggnad A" className="bg-[#080c12] border-white/[0.06] text-white" data-testid="project-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-white/40 text-sm">Arbetsnummer</Label>
                <Input value={projectForm.work_number} onChange={(e) => setProjectForm({ ...projectForm, work_number: e.target.value })}
                  placeholder="T.ex. P-2024-015" className="bg-[#080c12] border-white/[0.06] text-white" data-testid="project-work-number" />
              </div>
              {isAdmin && (
                <div className="space-y-1">
                  <Label className="text-white/40 text-sm">Timpris (kr)</Label>
                  <Input type="number" min="0" value={projectForm.hourly_rate}
                    onChange={(e) => setProjectForm({ ...projectForm, hourly_rate: e.target.value })}
                    placeholder="450" className="bg-[#080c12] border-white/[0.06] text-white" />
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-white/40 text-sm">Adress</Label>
              <Input value={projectForm.address} onChange={(e) => setProjectForm({ ...projectForm, address: e.target.value })}
                placeholder="Projektadress" className="bg-[#080c12] border-white/[0.06] text-white" />
            </div>
            <div className="space-y-1">
              <Label className="text-white/40 text-sm">Anteckningar</Label>
              <Input value={projectForm.notes} onChange={(e) => setProjectForm({ ...projectForm, notes: e.target.value })}
                placeholder="Övrigt..." className="bg-[#080c12] border-white/[0.06] text-white" />
            </div>
            {/* Contact persons */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-white/40 text-sm">Kontaktpersoner</Label>
                <Button type="button" size="sm" variant="outline" onClick={() => addContactPerson(setProjectForm, projectForm)}
                  className="border-white/[0.06] text-white/40 text-xs"><Plus className="w-3 h-3 mr-1" />Lägg till</Button>
              </div>
              {projectForm.contact_persons?.map((cp, idx) => (
                <div key={cp.name + cp.phone + idx} className="flex gap-2 items-center">
                  <Input value={cp.name} onChange={(e) => updateContactPerson(setProjectForm, projectForm, idx, 'name', e.target.value)}
                    placeholder="Namn" className="bg-[#080c12] border-white/[0.06] text-white flex-1" />
                  <Input value={cp.phone} onChange={(e) => updateContactPerson(setProjectForm, projectForm, idx, 'phone', e.target.value)}
                    placeholder="Telefon" className="bg-[#080c12] border-white/[0.06] text-white w-32" />
                  <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-white/40 hover:text-red-400"
                    onClick={() => removeContactPerson(setProjectForm, projectForm, idx)}><X className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setProjectDialogOpen(false)} className="border-white/[0.06] text-white/40">Avbryt</Button>
              <Button type="submit" disabled={submitting} className="bg-[#6dbf4a] hover:bg-[#5da83d]" data-testid="project-save-btn">
                {submitting ? "..." : "Spara"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Project Detail Modal */}
      <Dialog open={projectDetailOpen} onOpenChange={setProjectDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col bg-[#0d1218] border-white/[0.06]">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#6dbf4a]/20 flex items-center justify-center">
                <FolderKanban className="w-6 h-6 text-[#6dbf4a]" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-white">{selectedProject?.name}</DialogTitle>
                <p className="text-white/40">{selectedProject?.client_name}</p>
              </div>
            </div>
          </DialogHeader>

          {selectedProject && (
            <div className="flex-1 overflow-auto space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="bg-[#080c12] rounded-xl p-4">
                  <p className="text-sm text-white/40">Totalt</p>
                  <p className="text-2xl font-bold text-white">{projectStats.totalHours.toFixed(1)}h</p>
                </div>
                {isAdmin && (
                  <div className="bg-[#080c12] rounded-xl p-4">
                    <p className="text-sm text-white/40">Belopp</p>
                    <p className="text-2xl font-bold text-[#6dbf4a]">{projectStats.totalAmount.toLocaleString("sv-SE")} kr</p>
                  </div>
                )}
                <div className="bg-[#080c12] rounded-xl p-4">
                  <p className="text-sm text-white/40">Godkänt</p>
                  <p className="text-2xl font-bold text-emerald-400">{projectStats.approvedHours.toFixed(1)}h</p>
                </div>
              </div>

              {selectedProject.address && (
                <div className="flex items-center gap-2 text-sm text-white/40">
                  <MapPin className="w-4 h-4" />{selectedProject.address}
                </div>
              )}
              {selectedProject.notes && (
                <div className="flex items-start gap-2 text-sm text-white/40">
                  <FileText className="w-4 h-4 mt-0.5" />{selectedProject.notes}
                </div>
              )}

              {selectedProject.contact_persons?.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-white/40">Kontaktpersoner</h3>
                  {selectedProject.contact_persons.map((cp, idx) => (
                    <div key={cp.name + cp.phone + idx} className="bg-[#080c12] rounded-xl p-3 flex items-center gap-3">
                      <User className="w-4 h-4 text-white/40" />
                      <span className="text-white">{cp.name}</span>
                      {cp.phone && <><Phone className="w-3.5 h-3.5 text-white/40" /><span className="text-white/40">{cp.phone}</span></>}
                    </div>
                  ))}
                </div>
              )}

              {projectEntries.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-white/40 mb-2">Senaste tidsposter</h3>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/[0.06]">
                        <TableHead className="text-white/40">Datum</TableHead>
                        <TableHead className="text-white/40">Anställd</TableHead>
                        <TableHead className="text-white/40">Aktivitet</TableHead>
                        <TableHead className="text-white/40 text-right">Timmar</TableHead>
                        {isAdmin && <TableHead className="text-white/40 text-right">Belopp</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projectEntries.slice(0, 20).map(entry => (
                        <TableRow key={entry.id} className="border-white/[0.06]">
                          <TableCell className="text-white">{entry.date}</TableCell>
                          <TableCell className="text-white/40">{entry.user_name}</TableCell>
                          <TableCell className="text-white/40">{entry.activity_name}</TableCell>
                          <TableCell className="text-white text-right font-medium">{entry.hours}h</TableCell>
                          {isAdmin && (
                            <TableCell className="text-[#6dbf4a] text-right">
                              {entry.hourly_rate ? (entry.hours * entry.hourly_rate).toLocaleString("sv-SE") : "—"} kr
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;
